import { useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { Input, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { Banner, BannerDestType, BannerPlacement, CpEventListRow } from "../api/types";
import { UploadButton } from "../components/ui/UploadButton";

/* Round 60 - Banner Management. Where a banner shows (several allowed), what a tap does, when it is live and for whom.
   Everything is validated again on the server; the app asks the server what is live, so a saved change shows up on the next refresh. */

const PLACEMENTS: { id: BannerPlacement; label: string; hint: string }[] = [
  { id: "home", label: "Home screen", hint: "carousel on Home, reference size 735 × 200" },
  { id: "in_room", label: "In room", hint: "floating, bottom-right of a voice room, reference size 300 × 300" },
  { id: "ranking", label: "Ranking / Events", hint: "a link on the website Ranking page; no image needed" },
];
const IN_APP: { id: string; label: string }[] = [
  { id: "wallet", label: "Wallet" }, { id: "vip", label: "VIP" }, { id: "mall", label: "Mall" }, { id: "bag", label: "Bag" },
  { id: "daily_signin", label: "Daily sign-in" }, { id: "level", label: "Level" }, { id: "medals", label: "Medals" },
  { id: "club", label: "Club" }, { id: "games", label: "Games" }, { id: "moments", label: "Moments" },
];

type Draft = Partial<Banner> & { placements?: BannerPlacement[] };

const toLocalInput = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);

function liveState(b: Banner): { label: string; tone: "success" | "neutral" | "warn" | "danger" } {
  const now = Date.now();
  if (!b.is_active) return { label: "Disabled", tone: "neutral" };
  if (b.is_published === false) return { label: "Draft", tone: "neutral" };
  if (b.starts_at && new Date(b.starts_at).getTime() > now) return { label: "Scheduled", tone: "warn" };
  if (b.ends_at && new Date(b.ends_at).getTime() <= now) return { label: "Expired", tone: "danger" };
  return { label: "Live", tone: "success" };
}

function destLabel(b: Banner, cpEvents: CpEventListRow[]): string {
  switch (b.dest_type) {
    case "url": return b.dest_value || "website";
    case "event": {
      const ev = b.dest_slug === "cp" ? cpEvents.find((e) => String(e.id) === b.dest_value) : null;
      return `Event ${b.dest_slug || "cp"} #${b.dest_value}${ev ? ` — ${ev.title}` : ""}`;
    }
    case "ranking": return `Page /${b.dest_value || "ranking"}`;
    case "in_app": return `App: ${IN_APP.find((x) => x.id === b.dest_value)?.label ?? b.dest_value}`;
    default: return b.cp_event_id ? `CP event #${b.cp_event_id}` : "Not clickable";
  }
}

export function BannersPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<Banner>("/admin/banners");
  const [editing, setEditing] = useState<Draft | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Banner | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sizeNote, setSizeNote] = useState("");
  const { showToast } = useToast();
  const [cpEvents, setCpEvents] = useState<CpEventListRow[]>([]);
  useEffect(() => {
    apiRequest<CpEventListRow[]>("/admin/cp-events").then(setCpEvents).catch(() => setCpEvents([]));
  }, []);

  function open(b: Banner | null) {
    setSizeNote("");
    if (!b) { setEditing({ placements: ["home"], is_active: true, is_published: true, dest_type: "none", open_mode: "sheet", sort_order: 0 }); return; }
    // older rows (before Round 60) only have link_url / cp_event_id: show them as the destination they already were
    let dest_type = b.dest_type ?? "none", dest_value = b.dest_value ?? null, dest_slug = b.dest_slug ?? null;
    if (dest_type === "none" && b.cp_event_id) { dest_type = "event"; dest_value = String(b.cp_event_id); dest_slug = "cp"; }
    else if (dest_type === "none" && b.link_url) { dest_type = "url"; dest_value = b.link_url; }
    setEditing({ ...b, placements: b.placements?.length ? b.placements : ["home"], dest_type, dest_value, dest_slug, is_published: b.is_published ?? true, open_mode: b.open_mode ?? "sheet" });
  }

  function togglePlacement(p: BannerPlacement) {
    setEditing((cur) => {
      if (!cur) return cur;
      const has = cur.placements?.includes(p);
      const next = has ? (cur.placements ?? []).filter((x) => x !== p) : [...(cur.placements ?? []), p];
      return { ...cur, placements: next };
    });
  }

  function checkImageSize(url: string) {
    const img = new Image();
    img.onload = () => {
      const ratio = img.naturalWidth / img.naturalHeight, notes: string[] = [];
      const ps = editing?.placements ?? [];
      if (ps.includes("home") && Math.abs(ratio - 735 / 200) / (735 / 200) > 0.12) notes.push(`Home banners are 735 × 200 (about ${(735 / 200).toFixed(2)}:1); this image is ${img.naturalWidth} × ${img.naturalHeight}, so it will be cropped.`);
      if (ps.includes("in_room") && Math.abs(ratio - 1) > 0.12) notes.push(`In-room banners are square (300 × 300); this image is ${img.naturalWidth} × ${img.naturalHeight}, so it will be cropped.`);
      setSizeNote(notes.join(" "));
    };
    img.onerror = () => setSizeNote("This image address could not be loaded.");
    img.src = url;
  }

  async function saveBanner() {
    if (!editing) return;
    const e = editing;
    const placements = e.placements ?? [];
    if (!placements.length) return showToast("Choose at least one placement", "error");
    if (placements.some((p) => p !== "ranking") && !e.image_url) return showToast("An image is required for Home and In Room banners", "error");
    const t = (e.dest_type ?? "none") as BannerDestType;
    if (placements.includes("ranking") && t === "none") return showToast("A Ranking / Events entry needs an event or website page to open", "error");
    if (t === "url" && !/^https:\/\//i.test(e.dest_value ?? "")) return showToast("Website addresses must start with https://", "error");
    if ((t === "event") && !/^\d+$/.test(String(e.dest_value ?? ""))) return showToast("Enter the event id (a number)", "error");
    const body = {
      title: e.title ?? "", image_url: e.image_url || null, is_active: e.is_active ?? true, is_published: e.is_published ?? true,
      sort_order: Number(e.sort_order ?? 0), placements,
      starts_at: e.starts_at ?? null, ends_at: e.ends_at ?? null,
      dest_type: t, dest_value: t === "none" ? null : e.dest_value ?? null, dest_slug: t === "event" ? (e.dest_slug || "cp") : null,
      open_mode: e.open_mode ?? "sheet",
      audience_countries: e.audience_countries ?? [],
    };
    setIsSubmitting(true);
    try {
      if (e.id) await apiRequest(`/admin/banners/${e.id}`, { method: "PATCH", body });
      else await apiRequest("/admin/banners", { method: "POST", body });
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save banner", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/banners/${pendingDelete.id}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete", "error");
    } finally {
      setIsSubmitting(false);
      setPendingDelete(null);
    }
  }

  const columns: Column<Banner>[] = [
    {
      header: "Banner",
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.image_url ? <img src={row.image_url} className="h-12 w-24 rounded-lg object-cover" alt="" /> : <div className="flex h-12 w-24 items-center justify-center rounded-lg border border-dashed border-surface-border text-[10px] text-ink-faint">no image</div>}
          <span className="text-sm text-ink">{row.title || <span className="text-ink-faint">Untitled</span>}</span>
        </div>
      ),
    },
    {
      header: "Shows in",
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {(row.placements?.length ? row.placements : ["home"]).map((p) => <Badge key={p} tone="neutral">{PLACEMENTS.find((x) => x.id === p)?.label ?? p}</Badge>)}
        </div>
      ),
    },
    { header: "Opens", render: (row) => <span className="text-xs text-ink-faint">{destLabel(row, cpEvents)}</span> },
    {
      header: "Schedule",
      render: (row) => <span className="text-xs text-ink-faint">{row.starts_at || row.ends_at ? `${row.starts_at ? new Date(row.starts_at).toLocaleString() : "now"} → ${row.ends_at ? new Date(row.ends_at).toLocaleString() : "no end"}` : "Always"}</span>,
    },
    { header: "Order", render: (row) => <span className="text-xs">{row.sort_order}</span> },
    { header: "Status", render: (row) => { const s = liveState(row); return <Badge tone={s.tone}>{s.label}</Badge>; } },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => open(row)}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>Delete</Button>
        </div>
      ),
    },
  ];

  const t = (editing?.dest_type ?? "none") as BannerDestType;
  const needsImage = (editing?.placements ?? []).some((p) => p !== "ranking");

  return (
    <AppShell title="Banners">
      <div className="mb-4 flex justify-end">
        <Button onClick={() => open(null)}>+ Add Banner</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No banners yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Banner" : "Add Banner"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveBanner} isLoading={isSubmitting}>Save</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Internal name" value={editing?.title ?? ""} maxLength={80} onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))} />

          <fieldset>
            <legend className="mb-1.5 text-xs font-medium text-ink-muted">Show in (choose one or more)</legend>
            <div className="space-y-1.5">
              {PLACEMENTS.map((p) => (
                <label key={p.id} className="flex items-start gap-2 text-sm text-ink">
                  <input type="checkbox" className="mt-1" checked={!!editing?.placements?.includes(p.id)} onChange={() => togglePlacement(p.id)} />
                  <span>{p.label} <span className="text-xs text-ink-faint">— {p.hint}</span></span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="space-y-2">
            <Input label={needsImage ? "Image URL" : "Image URL (optional)"} value={editing?.image_url ?? ""} onChange={(e) => { setEditing((p) => ({ ...p, image_url: e.target.value })); }} onBlur={(e) => e.target.value && checkImageSize(e.target.value)} />
            <UploadButton kind="banner" onUploaded={(u) => { setEditing((p) => ({ ...p, image_url: u })); checkImageSize(u); }} />
            {sizeNote && <p className="rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn">{sizeNote}</p>}
            {editing?.image_url && <img src={editing.image_url} alt="" className="max-h-28 rounded-lg border border-surface-border object-contain" />}
          </div>

          <Select label="When tapped" value={t} onChange={(e) => setEditing((p) => ({ ...p, dest_type: e.target.value as BannerDestType, dest_value: null, dest_slug: e.target.value === "event" ? "cp" : null }))}>
            <option value="none">Nothing (not clickable)</option>
            <option value="event">Open an event page (by event id)</option>
            <option value="ranking">Open a website page (ranking / other)</option>
            <option value="url">Open a website address</option>
            <option value="in_app">Open an app screen</option>
          </Select>
          {t === "event" && (
            <div className="grid grid-cols-2 gap-3">
              <Input label="Event page" hint="cp = Weekly CP. A future event type uses its own page name." value={editing?.dest_slug ?? "cp"} onChange={(e) => setEditing((p) => ({ ...p, dest_slug: e.target.value.toLowerCase() }))} />
              {(editing?.dest_slug ?? "cp") === "cp" ? (
                <Select label="Event" value={editing?.dest_value ?? ""} onChange={(e) => { const ev = cpEvents.find((x) => String(x.id) === e.target.value); setEditing((p) => ({ ...p, dest_value: e.target.value, image_url: p?.image_url || ev?.bannerUrl || p?.image_url })); }}>
                  <option value="">Choose…</option>
                  {cpEvents.map((ev) => <option key={ev.id} value={ev.id}>{ev.title} — {ev.status}{ev.isEnabled ? "" : " (disabled)"}</option>)}
                </Select>
              ) : (
                <Input label="Event id" inputMode="numeric" value={editing?.dest_value ?? ""} onChange={(e) => setEditing((p) => ({ ...p, dest_value: e.target.value.replace(/\D/g, "") }))} />
              )}
            </div>
          )}
          {t === "ranking" && <Input label="Website page name" hint="ranking = the Ranking page" value={editing?.dest_value ?? "ranking"} onChange={(e) => setEditing((p) => ({ ...p, dest_value: e.target.value.toLowerCase() }))} />}
          {t === "url" && <Input label="Website address (https, hitowns.com only)" placeholder="https://h5.hitowns.com/events/…" value={editing?.dest_value ?? ""} onChange={(e) => setEditing((p) => ({ ...p, dest_value: e.target.value }))} />}
          {t === "in_app" && (
            <Select label="App screen" value={editing?.dest_value ?? ""} onChange={(e) => setEditing((p) => ({ ...p, dest_value: e.target.value }))}>
              <option value="">Choose…</option>
              {IN_APP.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </Select>
          )}
          {editing?.placements?.includes("in_room") && t !== "none" && t !== "in_app" && (
            <Select label="In a voice room, open it" value={editing?.open_mode ?? "sheet"} onChange={(e) => setEditing((p) => ({ ...p, open_mode: e.target.value as "sheet" | "full" }))}>
              <option value="sheet">In a bottom sheet over the room (room keeps running)</option>
              <option value="full">Full screen (leaves the room screen)</option>
            </Select>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Input label="Starts (your time zone)" type="datetime-local" value={toLocalInput(editing?.starts_at)} onChange={(e) => setEditing((p) => ({ ...p, starts_at: fromLocalInput(e.target.value) }))} />
            <Input label="Ends (your time zone)" type="datetime-local" value={toLocalInput(editing?.ends_at)} onChange={(e) => setEditing((p) => ({ ...p, ends_at: fromLocalInput(e.target.value) }))} />
          </div>
          <Input label="Display order (lower shows first)" type="number" value={String(editing?.sort_order ?? 0)} onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))} />
          <Input label="Audience: only these countries (optional)" hint="Two-letter codes separated by commas, e.g. IN, US. Empty = everyone." value={(editing?.audience_countries ?? []).join(", ")}
            onChange={(e) => setEditing((p) => ({ ...p, audience_countries: e.target.value.split(/[ ,]+/).map((s) => s.trim().toUpperCase()).filter(Boolean) }))} />
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={editing?.is_active ?? true} onChange={(e) => setEditing((p) => ({ ...p, is_active: e.target.checked }))} />
              Enabled
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={editing?.is_published ?? true} onChange={(e) => setEditing((p) => ({ ...p, is_published: e.target.checked }))} />
              Published (unticked = draft, not visible)
            </label>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isSubmitting}
        isDangerous
        title="Delete this banner?"
        description="This removes it from the app immediately."
        confirmLabel="Delete"
      />
    </AppShell>
  );
}
